import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from 'src/context/AuthContext'
import { useSnackData } from 'src/context/SnackContext'
import { ActionTypeEnum, actionsMapping, actionsSubmitFunctions } from './actions'

type ActionState = {
  currentStep: number
  collectedData: Record<string, any>
  dynamicOptions: Record<string, any[]>
  dynamicOptionsLoading: boolean
}

export const useActionsManager = (actionType: ActionTypeEnum) => {
  const { currentUser } = useAuth()
  const { openError, openSuccess } = useSnackData()
  const navigate = useNavigate()

  const [actionState, setActionState] = useState<ActionState>({
    currentStep: 0,
    collectedData: {},
    dynamicOptions: {},
    dynamicOptionsLoading: false
  })

  const actionConfig = useMemo(() => actionsMapping[actionType], [actionType])

  const isFinalStep = useMemo(() => {
    const nextStep = actionConfig?.steps[actionState.currentStep]?.on_select?.(actionState.collectedData)
    return actionState.currentStep === (actionConfig?.steps.length ?? 0) - 1 || nextStep === 'finish'
  }, [actionState.currentStep, actionConfig?.steps, actionState.collectedData])

  // Fetch dynamic options when step changes
  useEffect(() => {
    const fetchOptionsForCurrentStep = async () => {
      const currentStepConfig = actionConfig?.steps[actionState.currentStep]

      if (!currentStepConfig) {
        return
      }

      if (typeof currentStepConfig.step_options === 'function') {
        setActionState((prev) => ({ ...prev, dynamicOptionsLoading: true }))

        try {
          const options = await currentStepConfig.step_options(currentUser, actionState.collectedData)

          setActionState((prev) => ({
            ...prev,
            dynamicOptions: {
              ...prev.dynamicOptions,
              [currentStepConfig.step_id]: options
            },
            dynamicOptionsLoading: false
          }))
        } catch (error) {
          console.error('Error loading options:', error)
          openError('Error loading options')
          setActionState((prev) => ({ ...prev, dynamicOptionsLoading: false }))
        }
      } else {
        setActionState((prev) => ({
          ...prev,
          dynamicOptions: {
            ...prev.dynamicOptions,
            [currentStepConfig.step_id]: currentStepConfig.step_options
          },
          dynamicOptionsLoading: false
        }))
      }
    }

    fetchOptionsForCurrentStep()
  }, [actionState.currentStep, currentUser])

  const handleStepPrev = useCallback(() => {
    setActionState((prev) => ({
      ...prev,
      currentStep: prev.currentStep - 1
    }))
  }, [])

  const handleStepNext = useCallback(
    async (stepData: Record<string, any>) => {
      console.log('stepData', stepData)
      setActionState((prev) => ({
        ...prev,
        collectedData: { ...prev.collectedData, ...stepData }
      }))

      const currentStepConfig = actionConfig?.steps[actionState.currentStep]
      const selectedValue = stepData[currentStepConfig.step_id]

      // Determine next step based on selection
      let nextStepIndex = actionState.currentStep + 1

      if (currentStepConfig.on_select) {
        const nextStepId = currentStepConfig.on_select(selectedValue)
        if (nextStepId) {
          if (nextStepId === 'finish') {
            const updatedCollectedData = {
              ...actionState.collectedData,
              ...stepData
            }
            console.log('submitting with collectedData', updatedCollectedData)
            submitActionWithData(updatedCollectedData)
            return
          } else {
            const targetIndex = actionConfig?.steps.findIndex((s) => s.step_id === nextStepId)
            nextStepIndex = targetIndex >= 0 ? targetIndex : nextStepIndex
          }
        }
      }

      setActionState((prev) => ({
        ...prev,
        currentStep: nextStepIndex
      }))
    },
    [actionState.currentStep, actionState.collectedData, actionConfig?.steps]
  )

  const submitActionWithData = useCallback(
    async (collectedData: Record<string, any>) => {
      const submitFunction = actionsSubmitFunctions[actionType].submitFn
      try {
        const result = await submitFunction(currentUser, collectedData)
        openSuccess(result ?? 'Action submitted successfully')
        navigate('/home')
      } catch (error) {
        console.error('Error submitting action:', error)
        openError('Error submitting action')
        throw error
      }
    },
    [currentUser, actionType, openSuccess, openError, navigate]
  )

  const submitAction = useCallback(async () => {
    await submitActionWithData(actionState.collectedData)
  }, [actionState.collectedData, submitActionWithData])

  return {
    currentStep: actionConfig?.steps[actionState.currentStep] || null,
    actionState,
    handleStepNext,
    handleStepPrev,
    submitAction,
    isFinalStep: isFinalStep,
    dynamicOptionsLoading: actionState.dynamicOptionsLoading
  }
}
